import { ChevronLeft, ChevronRight, Crop, Download, Undo2 } from 'lucide-react';
import { useEffect, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { Media } from '@nucleus-commerce/api-client/admin';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@nucleus-commerce/ui/components/alert-dialog';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { useRevertMedia } from '../hooks/use-edit-media';
import { isEditableImage } from '../lib/image-edit';

import { MediaImageEditor } from './media-image-editor';
import { MediaThumb } from './media-thumb';

export interface MediaPreviewDialogProps {
  media: Media;
  title: ReactNode;
  description?: ReactNode;
  /** The files around this one, in order; the arrows walk them. */
  siblings?: readonly Media[];
  /** Move to another file; the caller keeps what was changed on this one first. */
  onNavigate?: (media: Media) => void;
  /** The details beside the picture: alt text and whatever else the caller edits. */
  panel: ReactNode;
  /** The bottom row while not editing the picture. */
  footer: ReactNode;
  /** May rewrite the file: `media.write`. */
  canEditImage: boolean;
  /** Queries that embed the media row, refreshed after an edit or a revert. */
  invalidates?: ReadonlyArray<readonly unknown[]>;
  /** The picture was edited or reverted. */
  onImageChanged?: (media: Media) => void;
  /** The X, Escape or a click outside. */
  onClose: () => void;
}

const previewSource = (media: Media) => media.renditions?.large ?? media.url;

function FullPreview({ media }: { media: Media }) {
  if (media.kind === 'image') {
    return (
      <img
        key={media.id}
        src={previewSource(media)}
        alt={media.alt ?? ''}
        className="max-h-full max-w-full object-contain"
      />
    );
  }
  if (media.kind === 'video') {
    return (
      <video key={media.id} src={media.url} controls className="max-h-full max-w-full">
        <track kind="captions" />
      </video>
    );
  }
  return <MediaThumb media={media} className="size-40" />;
}

function RevertButton({
  media,
  invalidates,
  onReverted,
}: {
  media: Media;
  invalidates: ReadonlyArray<readonly unknown[]>;
  onReverted: (media: Media) => void;
}) {
  const { t } = useTranslation(['media', 'common']);
  const revert = useRevertMedia({ invalidates });
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button type="button" size="sm" variant="outline" disabled={revert.isPending}>
          <Undo2 aria-hidden />
          {t('media:preview.revert')}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('media:preview.revertTitle')}</AlertDialogTitle>
          <AlertDialogDescription>{t('media:preview.revertBody')}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('common:actions.cancel')}</AlertDialogCancel>
          <AlertDialogAction onClick={() => revert.mutate(media.id, { onSuccess: onReverted })}>
            {t('media:preview.revertConfirm')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/**
 * The one file preview, Shopify style: the whole picture on a stage with arrows to the files around
 * it, the details beside it, and an editor that takes over the stage for crop, rotate, flip and
 * resize. An edit saves the file at once, like alt text; the caller's own footer handles the rest.
 */
export function MediaPreviewDialog({
  media,
  title,
  description,
  siblings = [],
  onNavigate,
  panel,
  footer,
  canEditImage,
  invalidates = [],
  onImageChanged,
  onClose,
}: MediaPreviewDialogProps) {
  const { t } = useTranslation('media');
  const [editing, setEditing] = useState(false);

  const index = siblings.findIndex((row) => row.id === media.id);
  const previous = index > 0 ? siblings[index - 1] : undefined;
  const next = index >= 0 ? siblings[index + 1] : undefined;
  const showArrows = onNavigate !== undefined && index >= 0 && siblings.length > 1;

  // The next file is usually one click away, so it should already be there.
  const nearby = [previous, next]
    .flatMap((row) => (row?.kind === 'image' ? [previewSource(row)] : []))
    .join(' ');
  useEffect(() => {
    if (!nearby) return;
    for (const src of nearby.split(' ')) new Image().src = src;
  }, [nearby]);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (editing || !showArrows || !e.currentTarget.contains(e.target as Node)) return;
    const target = e.target as HTMLElement;
    if (target.closest('input, textarea, select, video, [contenteditable="true"]')) return;
    const to = e.key === 'ArrowLeft' ? previous : e.key === 'ArrowRight' ? next : undefined;
    if (!to) return;
    e.preventDefault();
    onNavigate(to);
  };

  const changed = (updated: Media) => {
    setEditing(false);
    onImageChanged?.(updated);
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        onKeyDown={onKeyDown}
        // Escape and a stray click must not lose a crop: Escape only leaves the editor.
        onEscapeKeyDown={(e) => {
          if (!editing) return;
          e.preventDefault();
          setEditing(false);
        }}
        onInteractOutside={(e) => {
          if (editing) e.preventDefault();
        }}
        className="grid h-[calc(100dvh-2rem)] max-w-[calc(100%-2rem)] grid-cols-1 grid-rows-[auto_minmax(12rem,1fr)_minmax(0,1fr)_auto] gap-0 overflow-hidden p-0 sm:max-w-[min(80rem,calc(100%-2rem))] lg:grid-cols-[minmax(0,1fr)_22.5rem] lg:grid-rows-[auto_minmax(0,1fr)_auto]"
      >
        <DialogHeader className="col-span-full border-b px-6 py-4 pr-12 text-left">
          <DialogTitle className="truncate">{editing ? t('editor.title') : title}</DialogTitle>
          {/* Radix warns without a description; an empty one keeps the dialog labelled. */}
          <DialogDescription className={cn('truncate', !description && 'sr-only')}>
            {description ?? title}
          </DialogDescription>
        </DialogHeader>

        {editing ? (
          <MediaImageEditor
            media={media}
            src={previewSource(media)}
            invalidates={invalidates}
            onCancel={() => setEditing(false)}
            onSaved={changed}
          />
        ) : (
          <div className="bg-muted flex min-h-0 flex-col">
            <div className="relative flex min-h-0 flex-1 items-center justify-center p-4 sm:px-16 sm:py-6">
              <FullPreview media={media} />
              {showArrows ? (
                <>
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    className="absolute top-1/2 left-3 -translate-y-1/2 rounded-full"
                    aria-label={t('preview.previous')}
                    disabled={!previous}
                    onClick={() => previous && onNavigate(previous)}
                  >
                    <ChevronLeft aria-hidden />
                  </Button>
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    className="absolute top-1/2 right-3 -translate-y-1/2 rounded-full"
                    aria-label={t('preview.next')}
                    disabled={!next}
                    onClick={() => next && onNavigate(next)}
                  >
                    <ChevronRight aria-hidden />
                  </Button>
                  <span
                    className="bg-background/90 absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full border px-2.5 py-0.5 text-xs tabular-nums"
                    aria-live="polite"
                  >
                    {t('preview.position', { position: index + 1, count: siblings.length })}
                  </span>
                </>
              ) : null}
            </div>
            <div className="bg-background flex flex-wrap items-center justify-center gap-2 border-t px-4 py-2.5">
              {canEditImage && isEditableImage(media) ? (
                <Button type="button" size="sm" variant="outline" onClick={() => setEditing(true)}>
                  <Crop aria-hidden />
                  {t('preview.edit')}
                </Button>
              ) : null}
              {canEditImage && media.edited ? (
                <RevertButton media={media} invalidates={invalidates} onReverted={changed} />
              ) : null}
              <Button size="sm" variant="outline" asChild>
                <a
                  href={media.url}
                  download={media.filename ?? true}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Download aria-hidden />
                  {t('preview.download')}
                </a>
              </Button>
            </div>
          </div>
        )}

        {/* Stays mounted under the editor, so typed alt text outlives a crop. */}
        <aside
          className={cn(
            'min-h-0 overflow-y-auto border-t p-5 lg:border-t-0 lg:border-l',
            editing && 'hidden',
          )}
        >
          {panel}
        </aside>
        {editing ? null : (
          <div className="bg-muted/40 col-span-full flex flex-wrap items-center justify-end gap-2 border-t px-6 py-3">
            {footer}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
